import { ReactElement } from 'react';
import { FieldProps, ErrorMessage, useFormikContext, getIn } from 'formik';
import classnames from 'classnames';
import { tile } from '@interfaces/interfaces';
import styles from './TileRadio.module.scss';

interface iTileRadioInfo {
  caption: string;
  tileValue?: string;
}

interface iProps extends FieldProps {
  title?: string;
  isMandatory: boolean;
  tileData: tile[];
  orientationRow: boolean;
  mobileCollapseRow?: boolean;
  classname?: string;
  wrapTiles?: boolean;
  info?: iTileRadioInfo;
  tileChanged?: () => void;
}

const TileRadio = ({ title, isMandatory, field, form, tileData, orientationRow, mobileCollapseRow = true, classname = '', wrapTiles = false, info, tileChanged }: iProps): ReactElement => {
  const { errors, touched, values }: { errors: any; touched: any, values: any } = useFormikContext();
  const hasError = getIn(errors, field.name);
  const touchedError = getIn(touched, field.name);
  const tileLength = tileData.length;
  return (
    <>
      {title && (
        <div
          className={classnames({
            [styles.title]: true,
            [styles.titleError]: hasError && touchedError,
          })}>
          {title?.includes('\n\r')
          ? (
            <span className={styles.titleBold}>
              {title.split('\n\r')[0]}
              <br />
              <span style={{ fontWeight: 'normal' }}>{title.split('\n\r')[1]}</span>
            </span>
)
          : <span className={styles.titleBold}>{title}</span>}
          {!isMandatory && <span className={styles.optional}> ( optional )</span>}
        </div>
      )}
      <div
        className={classnames({
          [styles.tileContainer]: true,
          [styles.wrapTiles]: wrapTiles,
          [styles.orientationRow]: orientationRow && !mobileCollapseRow,
          [styles.orientationRowCollapse]: orientationRow && mobileCollapseRow,
          [styles.orientationColumn]: !orientationRow,
          [styles.tileItemExpand]: tileLength > 2,
        })}>
        {tileData.map((tileItem) => (
          <div
            key={tileItem.value}
            className={classnames({
            [styles.tileItem]: true,
            [styles.selected]: field.value === tileItem.value,
          })}>
            <input
              type="radio"
              className={classnames({
                [styles.radio]: true,
              })}
              id={`${field.name}${tileItem.value}`}
              name={field.name}
              value={tileItem.value}
              onClick={() => {
                form.setFieldTouched(field.name);
                form.setFieldValue(field.name, tileItem.value);
                // handle tileChanged only a tile is already selected and a new tile is now selected
                if (getIn(values, field.name) && tileItem.value !== getIn(values, field.name) && tileChanged) tileChanged();
              }}
            />
            <label htmlFor={`${field.name}${tileItem.value}`}>
              <div
                className={classnames({
                  [styles.tile]: true,
                  [`${classname}`]: true,
                })}>
                {tileItem.body}
              </div>
            </label>
          </div>
        ))}
        {tileData.map((tileItem) => (
          <div
            key={tileItem.value}
            className={classnames({
             [styles.noDisplay]: true,
          })}>
            {info && info.tileValue === tileItem.value && info.tileValue === getIn(values, field.name) && (
              <div
                key={`${tileItem.value}.info`}
                className={classnames({
                  [styles.rowBreak]: true,
                  [styles.noDisplay]: true,
                })}>
                {info && info.tileValue === tileItem.value && info.tileValue === getIn(values, field.name) && (<p className={styles.infoCaptionAboveTablet}>{info.caption}</p>)}
              </div>
            )}
          </div>
        ))}
        {info && info.tileValue === getIn(values, field.name) && (<p className={styles.infoCaptionMobile}>{info.caption}</p>)}
      </div>
      <div>
        <ErrorMessage className={styles.errorMsg} name={field.name} component="span" />
      </div>
    </>
  );
};

export default TileRadio;
